import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import useForm from 'react-hook-form';
import cs from 'classnames';
import { format } from 'date-fns';
import SearchIcon from '@material-ui/icons/Search';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, Button } from '@material-ui/core';
import humbs from 'humps';
import { getClaim, clearClaim } from '../../redux/modules/claim';
import DataTable from '../DataTable/DataTable';
import prepareClaims from './claim';
import prepareCascoClaims from './casco';
import InvoiceDetails from './InvoiceDetails';
import ClaimFrame from './ClaimFrame';
import { APPLICANT_TYPES, CLAIM_STATUS_TYPES, DATE_FORMAT_SHORT } from '../../constants';
import EfesContentHeader from '../EfesContentHeader';
import Loader from '../Loaders/Loader';
import useDeviceResize from '../../hooks/useDeviceResize';
import MainRadio from '../MUI/MainRadio';
import MainButton from '../MUI/MainButton';
import TextInputFieldWithValidation from '../ValidationFormElements/TextInputFieldWithValidation';
import DateInputFieldWithValidation from '../ValidationFormElements/DateInputFieldWithValidation';
import ClaimMobileView from './ClaimMobileView/index';
import claimStatusValidationSchema from '../../validations/claimStatusValidationSchema';
import useRegisterValidationFields from '../../hooks/useRegisterValidationFields';
import { getColumns } from './options';
import TextMaskCustom from '../MUI/TextMaskCustom';
import TitleTypography from '../MUI/TitleTypography';
import ScrollToTopPage from '../ScrollToTopPage';

import claimStatusSearch from '../../styles/svgs/claimStatus.svg';
import ClaimStatusIcon from '../../img/claims/claimStatus.svg';
import ClaimHeartIcon from '../../img/claims/heart.png';
import CascoCarIcon from '../../img/claims/cascoCar.png';
import TravelCalcHeaderImage from '../../img/travelHeader.png';
import AccidentIcon from '../../img/claims/accident.png';

import styles from './ClaimStatusCheck.module.scss';
import useStyles from './useStyles';

const getInsuranceTypeIcon = (label, classes) => {
  const mapping = {
    medicalClaim: <img src={ClaimHeartIcon} alt="medical" className={classes.claimButtonIcon} />,
    travelClaim: <img src={TravelCalcHeaderImage} alt="travel" className={classes.claimButtonIcon} />,
    accidentClaim: <img src={AccidentIcon} alt="accident" className={classes.claimButtonIcon} />,
    cascoClaim: <img src={CascoCarIcon} alt="casco" className={classes.cascoButtonIcon} />,
  };
  return mapping[label];
};

const ClaimStatusCheck = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();
  const isDeviceSmSize = useDeviceResize('sm');

  const { claims, isLoading } = useSelector((store) => store.claim, shallowEqual);

  const [claimNumber, setClaimNumber] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [openRows, setOpenRows] = useState({});
  const [hvhh, setHvhh] = useState('');
  const [insuranceType, setInsuranceType] = useState(CLAIM_STATUS_TYPES[0]);
  const [beneficiaryToAcc, setBeneficiaryToAcc] = useState(APPLICANT_TYPES[0].value);

  useEffect(() => {
    setOpenRows({});
  }, [claims]);

  const { register, handleSubmit, errors, clearError, setValue } = useForm({
    validationSchema: claimStatusValidationSchema,
    mode: 'onBlur',
  });
  useRegisterValidationFields(['clm', 'dob', 'isLegal', 'hvhh'], register);

  const isCasco = useMemo(
    () => !!(insuranceType && insuranceType.id === CLAIM_STATUS_TYPES[3].id),
    [insuranceType],
  );

  const showApplicantTypeSelection = insuranceType && [4].includes(insuranceType.id);

  useEffect(() => {
    setValue('isLegal', false);
  }, [register, setValue]);

  useEffect(() => () => dispatch(clearClaim()), [dispatch]);

  const preparedClaims = useMemo(() => {
    if (claims && claims[0].isError === 0) {
      return isCasco ? prepareCascoClaims(claims) : prepareClaims(claims);
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, isCasco, t]);

  const handleExpand = (row) => {
    setOpenRows((current) => {
      if (current[row.rowId]) {
        return { ...current, [row.rowId]: false };
      }
      return { ...current, [row.rowId]: true };
    });
  };

  const prepareAdditionalRows = (invoices) => invoices.map((inv) => ({
    ...inv,
    additionalRow: {
      data: { ...inv },
      Component: InvoiceDetails,
      isOpen: !!openRows[inv.rowId],
    },
  }));

  const handleInsTypeChange = useCallback(
    (insType) => {
      if (insType.id === insuranceType.id) {
        return;
      }

      setInsuranceType(insType);
      setBeneficiaryToAcc(APPLICANT_TYPES[0].value);
      setValue('isLegal', false);

      dispatch(clearClaim());
      setClaimNumber('');
      setValue('clm', '');
      setBirthDate(null);
      setValue('dob', null);
      setHvhh('');
      setValue('hvhh', '');
    },
    [dispatch, insuranceType.id, setValue],
  );

  const handleRadioChange = (val) => {
    setBeneficiaryToAcc(val);

    if (val === APPLICANT_TYPES[1].value) {
      setValue('isLegal', true);
    } else {
      setValue('isLegal', false);
    }
    setBirthDate(null);
    setValue('dob', null);
    setHvhh('');
    setValue('hvhh', '');
  };

  const onSubmit = (params) => {
    dispatch(getClaim({ ...params, isCasco, claimType: humbs.decamelize(insuranceType.label, { separator: '-' }) }));
  };

  return (
    <>
      <ScrollToTopPage />
      <EfesContentHeader
        headerImage={ClaimStatusIcon}
        headerText="claim.headerTitle"
        imageWidth={48}
      />
      <TitleTypography component="div" className={classes.headerTitle}>
        {t('claim.title')}
      </TitleTypography>
      <Grid container spacing={isDeviceSmSize ? 2 : 4}>
        {CLAIM_STATUS_TYPES.map((type) => {
          const isSelected = insuranceType && insuranceType.label === type.label;
          return (
            <Grid item xs={6} md={4} lg={3} key={type.label}>
              {isDeviceSmSize ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleInsTypeChange(type)}
                  className={cs(classes.insurance, {
                    [classes.insuranceActive]: isSelected,
                    [classes[type.label]]: isSelected,
                  })}
                >
                  <div>{getInsuranceTypeIcon(type.label, classes)}</div>
                  <Typography className={classes.insuranceText}>
                    {t(`claim.insuranceTypes.${type.label}`)}
                  </Typography>
                </div>
              ) : (
                <Button
                  className={cs(classes.insuranceBtn, {
                    [classes.insuranceActive]: isSelected,
                    [classes[type.label]]: isSelected,
                  })}
                  key={type.label}
                  disableTouchRipple
                  disableRipple
                  onClick={() => handleInsTypeChange(type)}
                  startIcon={getInsuranceTypeIcon(type.label, classes)}
                >
                  <Typography className={classes.insuranceText}>
                    {t(`claim.insuranceTypes.${type.label}`)}
                  </Typography>
                </Button>
              )}
            </Grid>
          );
        })}
      </Grid>

      <div id={styles.claimContainer}>
        <div className={styles.formWrapper}>
          {isLoading && (
            <div>
              <Loader />
            </div>
          )}
          <form className={styles.claimForm}>
            {insuranceType && (
              <>
                {showApplicantTypeSelection && (
                  <div className={styles.formSection}>
                    <div className={styles.radioBlock}>
                      {APPLICANT_TYPES.map(({ value, label }) => (
                        <div key={label} className={styles.radioField}>
                          <MainRadio
                            checked={value === beneficiaryToAcc}
                            onChange={({ e }) => {
                              handleRadioChange(Number(e.target.value));
                            }}
                            labelKey={label}
                            value={value}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Grid container spacing={isDeviceSmSize ? 1 : 4}>
                  <Grid item xs={12} sm={4} lg={3}>
                    <TextInputFieldWithValidation
                      placeholder="AA/00-000000"
                      formProps={{ errors, clearError }}
                      labelKey="claim.clmNumber"
                      name="clm"
                      value={claimNumber.toUpperCase()}
                      handleChange={(e) => {
                        const val = e.target.value;
                        setValue('clm', val, !!errors.clm);
                        setClaimNumber(val);
                      }}
                      required
                      InputProps={{
                        inputComponent: TextMaskCustom,
                        inputProps: {
                          handler: 'claimInputMask',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} lg={3}>
                    {beneficiaryToAcc && beneficiaryToAcc === APPLICANT_TYPES[1].value ? (
                      <TextInputFieldWithValidation
                        formProps={{ errors, clearError }}
                        labelKey="claim.hvhh"
                        name="hvhh"
                        value={hvhh}
                        handleChange={({ target }) => {
                          setValue(target.name, target.value, true);
                          setHvhh(target.value);
                        }}
                        required
                      />
                    ) : (
                      <DateInputFieldWithValidation
                        formProps={{ errors, clearError }}
                        labelKey="claim.dob"
                        name="dob"
                        value={birthDate}
                        handleChange={(date) => {
                          // eslint-disable-next-line no-restricted-globals
                          if (date instanceof Date && !isNaN(date)) {
                            setBirthDate(date);
                            setValue(
                              'dob',
                              date ? format(new Date(date), DATE_FORMAT_SHORT) : undefined,
                              true,
                            );
                          }
                        }}
                        maxDate={new Date()}
                        required
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <MainButton onClick={handleSubmit(onSubmit)} className={cs(classes.searchBtn)} color="primary" variant="contained">
                      {isDeviceSmSize ? (
                        <Typography className={classes.searchBtnText}>{t('search')}</Typography>
                      ) : (
                        <SearchIcon className={classes.searchBtnIcon} />
                      )}
                    </MainButton>
                  </Grid>
                </Grid>
              </>
            )}
          </form>
        </div>
        {!preparedClaims.length && (
          <div className={styles.infoBlcok}>
            <img className={styles.icon} src={claimStatusSearch} alt="" />
            <div className={styles.subText}>{t('claim.headerInfo')}</div>
          </div>
        )}
        {preparedClaims.map((claim) => {
          const infoLabelValue = isCasco
            ? { expertLabel: 'expert', claimCategory: claim.risks[0].risk }
            : { expertLabel: 'medExpert', claimCategory: claim.claimType };

          return (
            <React.Fragment key={claim.claimNumber}>
              <div className={styles.desktopContent}>
                <ClaimFrame claim={claim}>
                  <DataTable
                    data={prepareAdditionalRows(claim.invoices)}
                    columns={getColumns(isCasco, t)}
                    loading={isLoading}
                    paginated={false}
                    striped={false}
                    handleRowClick={handleExpand}
                  />
                </ClaimFrame>
              </div>
              <div className={styles.mobileContent}>
                <ClaimMobileView claim={claim} infoLabelValue={infoLabelValue} />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

export default ClaimStatusCheck;
